import { AdminTeamMember } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { ProUserProfileLink } from '../../components/ProfileLinks';
import { AdminActionsContainer } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { MemberSkillsChunked } from './TeamsResultsTab';
import { AdminTeams } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { InputDropdown } from '@platform/components';

const columnHelper = createColumnHelper<AdminTeamMember>();

export const FreeAgentsResultsTab = ({
  cohortId,
  setCreateTeamModalOpen,
  setEditMembersModalOpen,
}: {
  cohortId: number;
  setCreateTeamModalOpen: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; inComingMembers: undefined | AdminTeamMember[] }>
  >;
  setEditMembersModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      teamId: undefined | number;
      incomingNewMembers: undefined | AdminTeamMember[];
    }>
  >;
}) => {
  const { data: teamsIdsData } = AdminTeams.useGetTeamIds({ cohort_id: cohortId });
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { data, isSuccess, isLoading, isError } = AdminTeams.useGetFreeAgents(
    {
      cohort_id: cohortId,
      page: adminQueryOptionsV2.page,
      limit: adminQueryOptionsV2.limit,
    },
    { query: { placeholderData: keepPreviousData } },
  );
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'id',
          cell: (info) => `${info.getValue()}`,
          size: 10,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => (
            <ProUserProfileLink userId={row.original.id} name={row.original.name} />
          ),
        }),
        columnHelper.accessor('skills', {
          cell: ({ row }) => <MemberSkillsChunked skills={row.original.skills} />,
        }),
        columnHelper.display({
          id: 'addToTeam',
          cell: ({ row }) => (
            <AddToTeamCell
              row={row}
              setEditMembersModalOpen={setEditMembersModalOpen}
              teamIds={teamsIdsData?.teamIds || []}
            />
          ),
        }),
        columnHelper.display({
          id: 'actions',
          cell: ({ row }) => (
            <FreeAgentActionsCell row={row} setCreateTeamModalOpen={setCreateTeamModalOpen} />
          ),
        }),
      ] as ColumnDef<AdminTeamMember>[],
    [setCreateTeamModalOpen, setEditMembersModalOpen, teamsIdsData],
  );

  return (
    <div className='mt-2'>
      <AdminTable
        data={data?.freeAgents || []}
        columns={columns}
        filteredCount={data?.total || 0}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
};

const AddToTeamCell = ({
  row,
  setEditMembersModalOpen,
  teamIds,
}: {
  row: Row<AdminTeamMember>;
  setEditMembersModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      teamId: undefined | number;
      incomingNewMembers: undefined | AdminTeamMember[];
    }>
  >;
  teamIds: number[];
}) => {
  return (
    <InputDropdown
      type='default'
      items={teamIds.map((teamId) => ({
        text: `id: ${teamId}`,
        textValue: teamId,
      }))}
      onSelectionChange={(teamId) => {
        setEditMembersModalOpen({
          isOpen: true,
          teamId: teamId as number,
          incomingNewMembers: [row.original],
        });
      }}
      placeholder='Add to team'
    />
  );
};

const FreeAgentActionsCell = ({
  row,
  setCreateTeamModalOpen,
}: {
  row: Row<AdminTeamMember>;
  setCreateTeamModalOpen: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; inComingMembers: undefined | AdminTeamMember[] }>
  >;
}) => {
  return (
    <AdminActionsContainer
      actions={[
        {
          text: 'Start New Team',
          onPress: () => setCreateTeamModalOpen({ isOpen: true, inComingMembers: [row.original] }),
          type: 'button',
        },
      ]}
    />
  );
};
